/**
 * Imperative sign-in / sign-up / sign-out operations. Each returns an
 * `AuthedUser` (or throws `UserAccessError`).
 *
 * Consumers should never need to handle Firebase-specific errors — the
 * `mapFirebaseError` helper normalizes every known code into the closed
 * `UserAccessErrorCode` union.
 */

import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type UserCredential,
} from 'firebase/auth';
import { getAuthInstance, getContinueUrl } from './client';
import { mapFirebaseError } from './errors';
import { type AuthedUser, type OAuthProvider } from './types';

/**
 * Where an email action link returns the user once Firebase has completed the
 * action. `undefined` when the app configured no `continueUrl` — Firebase then
 * uses the project default, which is what every app did before this existed.
 *
 * `undefined` rather than `null` because the two Firebase senders disagree:
 * `sendEmailVerification` accepts `ActionCodeSettings | null`, but
 * `sendPasswordResetEmail` accepts only `ActionCodeSettings | undefined`.
 *
 * Note this controls the *landing page*, not the link's own host. The host is
 * the Auth action-URL domain, which is console configuration (STEM-363).
 */
function continueSettings(): { url: string } | undefined {
  const url = getContinueUrl();
  return url ? { url } : undefined;
}

async function toAuthedUser(cred: UserCredential, defaultIsNew: boolean): Promise<AuthedUser> {
  const info = getAdditionalUserInfo(cred);
  const isNew = typeof info?.isNewUser === 'boolean' ? info.isNewUser : defaultIsNew;
  return {
    uid: cred.user.uid,
    email: cred.user.email,
    emailVerified: cred.user.emailVerified,
    displayName: cred.user.displayName,
    photoURL: cred.user.photoURL,
    idToken: await cred.user.getIdToken(),
    isNew,
  };
}

export async function login(email: string, password: string): Promise<AuthedUser> {
  try {
    const cred = await signInWithEmailAndPassword(getAuthInstance(), email, password);
    return toAuthedUser(cred, false);
  } catch (err) {
    throw mapFirebaseError(err);
  }
}

export async function signup(email: string, password: string): Promise<AuthedUser> {
  try {
    const cred = await createUserWithEmailAndPassword(getAuthInstance(), email, password);
    return toAuthedUser(cred, true);
  } catch (err) {
    throw mapFirebaseError(err);
  }
}

export async function loginWithProvider(provider: OAuthProvider): Promise<AuthedUser> {
  const stripeProvider = buildProvider(provider);
  try {
    const cred = await signInWithPopup(getAuthInstance(), stripeProvider);
    return toAuthedUser(cred, false);
  } catch (err) {
    throw mapFirebaseError(err);
  }
}

function buildProvider(provider: OAuthProvider): GoogleAuthProvider {
  switch (provider) {
    case 'google':
      return new GoogleAuthProvider();
    default:
      // Exhaustiveness guard — OAuthProvider is a closed union.
      throw new Error(`Unsupported OAuth provider: ${String(provider)}`);
  }
}

export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(getAuthInstance());
  } catch (err) {
    throw mapFirebaseError(err);
  }
}

export async function sendVerificationEmail(): Promise<void> {
  const user = getAuthInstance().currentUser;
  if (!user) {
    throw new Error('No signed-in user found to send a verification email.');
  }

  try {
    await sendEmailVerification(user, continueSettings());
  } catch (err) {
    throw mapFirebaseError(err);
  }
}

export async function sendPasswordResetEmail(email: string): Promise<void> {
  try {
    await firebaseSendPasswordResetEmail(getAuthInstance(), email, continueSettings());
  } catch (err) {
    throw mapFirebaseError(err);
  }
}
